import LegalDocumentLayout from '@/app/Legal/LegalDocumentLayout';

const PrivacyPolicyPage = () => (
  <LegalDocumentLayout
    title="Privacy Policy"
    description="Privacy terms for DwellBot Games and PlayClaw.Live, using the current policy content from the DwellBot website."
  >
    <p>
      <strong>Effective date:</strong> February 19, 2026
      <br />
      <strong>Last updated:</strong> April 7, 2026
    </p>

    <p>
      This Privacy Policy explains how <strong>Dwell Bot Inc.</strong> (doing business as{' '}
      <strong>DwellBot Games</strong>) (&quot;<strong>DwellBot</strong>,&quot; &quot;<strong>we</strong>,&quot;{' '}
      &quot;<strong>us</strong>,&quot; &quot;<strong>our</strong>&quot;) collects, uses, shares, and protects
      information when you use our websites, apps, and related services (collectively, the{' '}
      &quot;<strong>Services</strong>&quot;), including:
    </p>

    <ul>
      <li>
        <strong>DwellBot Games</strong> (our brand and related apps/experiences)
      </li>
      <li>
        <strong>PlayClaw.Live</strong> (website and gameplay experience)
      </li>
    </ul>

    <p>
      If you have questions, contact us at <a href="mailto:privacy@dwell.bot">privacy@dwell.bot</a>.
    </p>

    <hr />

    <h2>Quick summary (plain English)</h2>
    <ul>
      <li>
        We collect information you provide (like your email), information from social login (Google/Facebook), and
        basic usage/device data to run and secure the Services.
      </li>
      <li>
        If you use real-time features (e.g., live video/audio, gameplay sessions), we process that data to provide the
        experience and keep it reliable and safe.
      </li>
      <li>
        We use <strong>Clerk</strong> for authentication, including Google and Facebook sign-in.
      </li>
      <li>We do not sell your personal information.</li>
      <li>You can request access, deletion, or correction of your data.</li>
    </ul>

    <hr />

    <h2>1) Who we are</h2>
    <p>
      <strong>Controller:</strong> Dwell Bot Inc. (DBA DwellBot Games)
      <br />
      <strong>Contact:</strong> <a href="mailto:privacy@dwell.bot">privacy@dwell.bot</a>
      <br />
      <strong>Mailing address:</strong> 1333 North Adams Street, Tallahassee, FL 32303
    </p>

    <hr />

    <h2>2) Information we collect</h2>
    <p>
      We collect information in three ways: (A) what you provide, (B) what we receive from third parties (like social
      login), and (C) what we collect automatically.
    </p>

    <h3>A) Information you provide</h3>
    <p>Depending on how you use the Services, you may provide:</p>
    <ul>
      <li>
        <strong>Account information:</strong> name, email address, username/handle, profile image.
      </li>
      <li>
        <strong>Support and communications:</strong> messages you send us, feedback, bug reports.
      </li>
      <li>
        <strong>Content you submit:</strong> any text, images, or other content you choose to upload or share through
        the Services.
      </li>
      <li>
        <strong>Payment information (if applicable):</strong> if you purchase something, payments are typically
        handled by a payment processor; we generally do not store full card numbers ourselves.
      </li>
    </ul>

    <h3>B) Information from social login (Google/Facebook) via Clerk</h3>
    <p>
      If you choose &quot;Sign in with Google&quot; or &quot;Sign in with Facebook,&quot; we receive limited profile
      information from those providers <strong>through Clerk</strong>, which may include:
    </p>
    <ul>
      <li>
        <strong>Email address</strong>
      </li>
      <li>
        <strong>Name</strong>
      </li>
      <li>
        <strong>Profile photo (if available)</strong>
      </li>
      <li>
        <strong>Provider user ID / identifier</strong>
      </li>
      <li>
        <strong>Authentication metadata</strong> (e.g., sign-in timestamps; we do not receive your password)
      </li>
    </ul>

    <p>We use this information to create/manage your account and authenticate you.</p>

    <blockquote>
      <p>
        We do <strong>not</strong> post to your social accounts. We do <strong>not</strong> access your friends list
        or private messages unless you explicitly grant a permission and we clearly explain it at the time.
      </p>
    </blockquote>

    <h3>C) Information collected automatically</h3>
    <p>When you use the Services, we may automatically collect:</p>
    <ul>
      <li>
        <strong>Device and network data:</strong> IP address, device type, operating system, browser type, app
        version, language, and approximate location (derived from IP).
      </li>
      <li>
        <strong>Usage data:</strong> pages/screens viewed, gameplay/session events, features used, clicks, session
        duration, referrers, crash logs, performance diagnostics.
      </li>
      <li>
        <strong>Cookies and similar technologies:</strong> for login/session management, security, preferences, and
        analytics (see Section 8).
      </li>
    </ul>

    <h3>D) Real-time features (if you use them)</h3>
    <p>
      Some Services may include real-time features (for example: live gameplay, livestream-style viewing, chat, or
      other interactive sessions). If you use these features, we may process:
    </p>
    <ul>
      <li>
        <strong>Live video and images</strong> from game cameras and/or your device camera (if enabled)
      </li>
      <li>
        <strong>Audio</strong> from your device microphone (if enabled)
      </li>
      <li>
        <strong>Real-time messages/events</strong> (e.g., chat messages, control inputs, session state)
      </li>
      <li>
        <strong>Connection/quality telemetry</strong> (latency, packet loss, connection status) to keep sessions
        working well
      </li>
    </ul>

    <p>We use this data to provide the experience, prevent abuse/fraud, and maintain safety and reliability.</p>

    <hr />

    <h2>3) How we use information</h2>
    <p>We use information for the following purposes:</p>
    <ol>
      <li>
        <strong>Provide and operate the Services</strong>
        <br />
        Create accounts, authenticate users, maintain sessions, and deliver requested features.
      </li>
      <li>
        <strong>Security, safety, and integrity</strong>
        <br />
        Detect, prevent, and respond to abuse, fraud, cheating, and unauthorized access. Enforce our terms and protect
        users and the public.
      </li>
      <li>
        <strong>Improve and debug</strong>
        <br />
        Measure performance, fix bugs, improve reliability and user experience.
      </li>
      <li>
        <strong>Communicate with you</strong>
        <br />
        Send service/transactional messages (e.g., verification, security alerts, important updates). Respond to
        support requests.
      </li>
      <li>
        <strong>Comply with law</strong>
        <br />
        Meet legal obligations and respond to lawful requests.
      </li>
      <li>
        <strong>With your consent</strong>
        <br />
        For optional features or marketing communications where required by law.
      </li>
    </ol>

    <hr />

    <h2>4) Legal bases (EEA/UK and similar regions)</h2>
    <p>Where applicable, we process personal data under these legal bases:</p>
    <ul>
      <li>
        <strong>Contract:</strong> to provide the Services you request.
      </li>
      <li>
        <strong>Legitimate interests:</strong> to secure and improve the Services, prevent abuse, and operate our
        business.
      </li>
      <li>
        <strong>Consent:</strong> where required (e.g., certain cookies, optional marketing).
      </li>
      <li>
        <strong>Legal obligation:</strong> to comply with applicable laws.
      </li>
    </ul>

    <hr />

    <h2>5) How we share information</h2>
    <p>We share information only as needed to operate the Services and comply with law.</p>

    <h3>A) Service providers (processors)</h3>
    <p>We use vendors to help us run the Services. Examples may include:</p>
    <ul>
      <li>
        <strong>Authentication / identity:</strong> Clerk (user management, SSO)
      </li>
      <li>
        <strong>Hosting / infrastructure / storage:</strong> [List your hosts/storage providers]
      </li>
      <li>
        <strong>Analytics / monitoring:</strong> Mixpanel, Meta Pixel / Meta Conversions API, and service providers
        we use for operational monitoring and error tracking
      </li>
      <li>
        <strong>Customer support tools:</strong> [List support providers, if any]
      </li>
      <li>
        <strong>Payments (if applicable):</strong> [List payment providers]
      </li>
    </ul>

    <p>
      These providers are authorized to process personal data only as necessary to provide services to us and are
      required to protect it.
    </p>

    <h3>B) Social login providers</h3>
    <p>
      If you choose Google or Facebook sign-in, those providers may receive information about your use of their login
      features. Their handling is governed by their own privacy policies.
    </p>

    <h3>C) Legal, safety, and enforcement</h3>
    <p>We may disclose information if we believe it is reasonably necessary to:</p>
    <ul>
      <li>Comply with law, regulation, legal process, or governmental requests</li>
      <li>Enforce our terms and policies</li>
      <li>Protect the rights, property, and safety of users, DwellBot, and the public</li>
    </ul>

    <h3>D) Business transfers</h3>
    <p>
      If we are involved in a merger, acquisition, financing, reorganization, bankruptcy, or sale of assets,
      information may be transferred as part of that transaction, subject to standard confidentiality protections.
    </p>

    <h3>E) With your direction</h3>
    <p>
      We share information when you instruct us to do so (for example, integrating with a third-party app you
      connect).
    </p>

    <p>
      <strong>We do not sell your personal information.</strong>
      <br />
      We also do not share personal information for cross-context behavioral advertising in the way certain laws define
      it.
    </p>

    <hr />

    <h2>6) Data retention</h2>
    <p>We retain personal data only as long as needed for the purposes described in this Policy, including:</p>
    <ul>
      <li>Maintaining your account and providing the Services</li>
      <li>Security and fraud prevention</li>
      <li>Compliance with legal obligations</li>
      <li>Resolving disputes and enforcing agreements</li>
    </ul>

    <p>Examples:</p>
    <ul>
      <li>
        <strong>Account data:</strong> retained while your account is active and for a limited period after deletion
        where necessary for legal, security, or fraud-prevention purposes.
      </li>
      <li>
        <strong>Logs/security data:</strong> typically retained for a limited period (often days to months) unless
        needed for investigations.
      </li>
      <li>
        <strong>Real-time session data:</strong> processed to provide the session; if we store recordings or replays,
        we retain them until you delete them or for a limited period based on product needs and legal requirements.
      </li>
      <li>
        <strong>Backups:</strong> may persist for a limited time even after deletion, then are overwritten.
      </li>
    </ul>

    <hr />

    <h2>7) Security</h2>
    <p>
      We use reasonable administrative, technical, and organizational safeguards designed to protect information (e.g.,
      encryption in transit, access controls, least-privilege practices, monitoring). No method of transmission or
      storage is 100% secure, but we work to protect your data.
    </p>

    <hr />

    <h2>8) Cookies and similar technologies</h2>
    <p>We use cookies and similar technologies for:</p>
    <ul>
      <li>
        <strong>Essential functions:</strong> authentication, session management, security (including Clerk cookies)
      </li>
      <li>
        <strong>Preferences:</strong> saving settings such as language or gameplay preferences
      </li>
      <li>
        <strong>Analytics:</strong> understanding usage and improving the Services
      </li>
    </ul>

    <p>
      You can control cookies through your browser settings. Some features may not function properly if you disable
      essential cookies.
    </p>

    <p>If you use analytics tools, you should list them here and provide opt-out instructions where applicable:</p>
    <ul>
      <li>
        <strong>Analytics provider(s):</strong> Mixpanel and Meta Pixel / Meta Conversions API
      </li>
      <li>
        <strong>Purpose:</strong> product analytics, funnel measurement, performance monitoring, and ad attribution
      </li>
      <li>
        <strong>Opt-out:</strong> browser cookie controls, browser privacy settings, and controls provided by the
        applicable platform provider such as Meta
      </li>
    </ul>

    <hr />

    <h2>9) International data transfers</h2>
    <p>
      If you access the Services from outside the United States, your information may be processed in countries with
      different data protection laws. Where required, we use safeguards such as standard contractual clauses.
    </p>

    <hr />

    <h2>10) Your rights and choices</h2>
    <p>Depending on where you live, you may have rights to:</p>
    <ul>
      <li>
        <strong>Access</strong> the personal data we hold about you
      </li>
      <li>
        <strong>Correct</strong> inaccurate data
      </li>
      <li>
        <strong>Delete</strong> your data
      </li>
      <li>
        <strong>Object</strong> to certain processing or request restriction
      </li>
      <li>
        <strong>Portability</strong> (receive a copy in a usable format)
      </li>
      <li>
        <strong>Withdraw consent</strong> (where processing is based on consent)
      </li>
    </ul>

    <p>
      To exercise rights, contact <a href="mailto:privacy@dwell.bot">privacy@dwell.bot</a>. We may verify your
      identity before fulfilling requests.
    </p>

    <h3>Camera/microphone controls</h3>
    <p>
      If the Services request camera or microphone access, you can deny access in your device/browser settings. Some
      real-time features may not work without these permissions.
    </p>

    <hr />

    <h2>11) Data deletion instructions (Facebook / Meta requirement)</h2>
    <p>You can request deletion in any of these ways:</p>
    <ol>
      <li>
        <strong>In-app deletion (if available):</strong>
        <br />
        Go to <strong>Settings -&gt; Account -&gt; Delete Account</strong> and follow the prompts.
      </li>
      <li>
        <strong>Email request:</strong>
        <br />
        Email <a href="mailto:privacy@dwell.bot">privacy@dwell.bot</a> with subject{' '}
        <strong>&quot;Data Deletion Request&quot;</strong> from the email address associated with your account.
        Include:
        <ul>
          <li>The email address you used for the Services</li>
          <li>Your request (e.g., &quot;delete my account and associated personal data&quot;)</li>
        </ul>
      </li>
      <li>
        <strong>Facebook Login deletion request:</strong>
        <br />
        Email <a href="mailto:privacy@dwell.bot">privacy@dwell.bot</a> with subject{' '}
        <strong>&quot;Facebook Login Data Deletion Request&quot;</strong> and include:
        <ul>
          <li>The email address used in our Services</li>
          <li>Your Facebook name and/or Facebook user ID (if available)</li>
        </ul>
      </li>
    </ol>

    <p>
      We will delete or anonymize your personal data unless we must retain certain information for legal, security, or
      fraud-prevention reasons.
    </p>

    <hr />

    <h2>12) Children&apos;s privacy</h2>
    <p>
      The Services are not directed to children under <strong>13</strong> (or under <strong>16</strong> in certain
      regions), and we do not knowingly collect personal data from children. If you believe a child has provided us
      personal data, contact <a href="mailto:privacy@dwell.bot">privacy@dwell.bot</a> and we will take appropriate
      steps to delete it.
    </p>

    <hr />

    <h2>13) Google API Services: Limited Use disclosure</h2>
    <p>
      If we receive information from <strong>Google API Services</strong> (including via Google Sign-In), our use and
      transfer of that information will comply with the <strong>Google API Services User Data Policy</strong>,
      including the <strong>Limited Use</strong> requirements.
    </p>

    <p>In practice:</p>
    <ul>
      <li>We use Google user data only to provide or improve user-facing features in the Services.</li>
      <li>We do not sell Google user data.</li>
      <li>We do not use Google user data for advertising profiling.</li>
      <li>
        We do not use Google user data to train generalized AI models unless you explicitly consent and it is clearly
        described.
      </li>
    </ul>

    <hr />

    <h2>14) Third-party links</h2>
    <p>
      The Services may contain links to third-party sites or services. We are not responsible for their privacy
      practices. Please review their privacy policies.
    </p>

    <hr />

    <h2>15) Changes to this Privacy Policy</h2>
    <p>
      We may update this Policy from time to time. We will post the updated version and revise the &quot;Last
      updated&quot; date above. If changes are material, we will provide additional notice as required by law.
    </p>

    <hr />

    <h2>16) Contact us</h2>
    <p>
      <strong>Dwell Bot Inc. (DBA DwellBot Games)</strong>
      <br />
      <strong>Email:</strong> <a href="mailto:privacy@dwell.bot">privacy@dwell.bot</a>
      <br />
      <strong>Mailing address:</strong> 1333 North Adams Street, Tallahassee, FL 32303
    </p>

    <hr />

    <h2>Optional addendum: U.S. state privacy notice (if applicable)</h2>
    <p>
      If you meet certain thresholds under laws like the California Consumer Privacy Act (CCPA/CPRA), you may need an
      additional notice describing categories of personal information collected, retention, and consumer rights
      (including &quot;Do Not Sell/Share&quot;). If you&apos;d like, you can add a state addendum here.
    </p>
  </LegalDocumentLayout>
);

export default PrivacyPolicyPage;
